import { regNumberLabel } from '../../../packages/core/src/countries.ts';
import { StreamCollector } from '../../../packages/stream/src/fountain.ts';
import { verifyPacketBytes, type Verification } from '../../../packages/core/src/verify.ts';
import type { Check, CheckKey } from '../../../packages/core/src/verify.ts';
import { findKey, type IssuerDocument } from '../../../packages/core/src/issuer.ts';
import { isArticle, openPayload, parseEnvelope, verifyEnvelope, type NetIdPacket } from '../../../packages/core/src/packet.ts';
import { LivenessTracker, type LivenessState } from '../../../packages/core/src/liveness.ts';
import { verifyCompany, type CompanyCheck, type CompanyVerification } from '../../../packages/core/src/company.ts';
import type { Fetch } from '../../../packages/core/src/chain.ts';
import { addressCheck, subdomainExamples, type AddressCheck } from '../../../packages/core/src/address.ts';
import { decodeTick, TickTracker, type TickState } from '../../../packages/core/src/tick.ts';
import { syncClock, type ClockEstimate } from '../../../packages/core/src/clock.ts';
import { fromBase64Url, toBase64Url } from '../../../packages/core/src/bytes.ts';
import { encodeTick } from '../../../packages/core/src/tick.ts';

/**
 * The PageID scanner. Everything after the camera runs on the phone: QR
 * decoding (worker), fountain decoding, the Ed25519 signatures, the liveness
 * rule (≥ 3 distinct freshly signed codes in step with this phone's clock),
 * the SHA-256 of the article text, the Concordium lookups (straight to CCDScan
 * and the wallet-proxy) and the company's identity proof (WASM, with the
 * identity provider's public keys).
 *
 * Address step (relay protection, layer 1; SPEC.md §7.1): after a scan that
 * passed, the reader must compare the code's page domain with their browser's
 * address bar BEFORE the certificate. A relayed code (a fake site streaming the
 * real page's code) passes every check the camera can do; only the address bar
 * tells. Asked on every scan, for every domain: a relay of a known domain is
 * exactly the attack.
 *
 * Copy check (relay protection, layers 2 and 3; SPEC.md §7.2): a v3 code
 * belongs to one page view's stream session. Each QR frame carries a signed
 * tick, checked against PageID's clock (synced on open) to 2.5 s, and the
 * scanner tells PageID's server it saw the session (with the tick as proof).
 * A session several phones scanned is a copy streamed to many people: red,
 * never a certificate. A result screen keeps asking for ~30 s, so a session
 * revoked after this scan still turns the screen red.
 *
 * Any failing verdict (a recording, stale ticks, a copied / shared / revoked session, a bad signature, an
 * unknown key, the address answered "No", the company checks failing) shows ONE full-screen red result: "✗ This
 * code is not valid", one plain line with the reason, "Don't trust this page." and "Scan again". Company,
 * domain, certificate and check list are only behind a collapsed "Technical details".
 *
 * Proof screen: the COMPANY first (as its identity provider verified it, or
 * "pending onboarding"), for the page on the CONFIRMED domain, then the page,
 * then the article.
 */
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const screens = ['start', 'scan', 'verifying', 'address', 'invalid', 'proof'] as const;
type Screen = (typeof screens)[number];

function show(s: Screen) {
  for (const id of screens) $(id).hidden = id !== s;
  // The red screen's technical details are built per result; gone with it.
  if (s !== 'invalid') $('invalid-tech-body').replaceChildren();
  document.body.dataset.screen = s;
}

const collector = new StreamCollector();
const worker = new Worker(new URL('./qr-worker.js', import.meta.url), { type: 'module' });
const liveness = new LivenessTracker();
/** Completed streams, in order; a completed stream showing up again goes to the liveness rule too. */
const doneStreams = new Set<number>();
let lastDone: number | null = null;
let busy = false;
let stream: MediaStream | null = null;
let scanning = false;
let engine = '';
let decodedQrs = 0;
let lastResult: ScanResult | null = null;
let lastPacketBytes: Uint8Array | null = null;
let live: LivenessState = liveness.state();
const ticks = new TickTracker();
/** The v3 session of the code being scanned (from its verified packet), null for older codes. */
let sess: { id: Uint8Array; pub: Uint8Array; idText: string; packet: NetIdPacket; bytes: Uint8Array } | null = null;
let tickState: TickState | null = null;
let clock: ClockEstimate | null = null;
const clockReady = syncClock(async () => {
  const r = await fetch('/api/time', { cache: 'no-store' });
  return ((await r.json()) as { t: number }).t;
}).then((c) => (clock = c)).catch(() => null);
/** This phone's clock, corrected to PageID's (clock.ts); the raw clock when the sync failed. */
const nowC = () => Date.now() + (clock?.offsetMs ?? 0);

/** A random id made once per scanner install (no personal data): what the server counts per session. */
const scannerId = (() => {
  try {
    const old = localStorage.getItem('pageid-scanner-id');
    if (old && /^[A-Za-z0-9_-]{22}$/.test(old)) return old;
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    const id = toBase64Url(b);
    localStorage.setItem('pageid-scanner-id', id);
    return id;
  } catch {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    return toBase64Url(b);
  }
})();

/** The server's answer about the scanned session (layer 2), or why there is none. */
export type CopyCheck = {
  state: 'unique' | 'shared' | 'shared-nearby' | 'revoked' | 'session-unknown' | 'expired' | 'offline' | 'legacy';
  reason?: string;
  datacenter?: string | null;
  near_page?: boolean;
  scanners?: number;
};
let copyCheck: Promise<CopyCheck> | null = null;

export type ScanResult = Verification & {
  live: LivenessState;
  /** v3 codes: the tick rule (layer B); null for older codes (the packet liveness rule applies). */
  ticks: TickState | null;
  copy: CopyCheck;
  company: CompanyVerification | null;
  /** ok = everything verified; pending = a live, signed code from a company that has not finished onboarding; bad = do not trust. */
  verdict: 'ok' | 'pending' | 'bad';
  /** The code's page address, as the reader must compare it (null: the code names no usable page). */
  address: AddressCheck | null;
  /** The reader's answer to "does your address bar show exactly <domain>?" (null: not asked / not answered yet). */
  confirmed: boolean | null;
};

// ---- customer documents ----------------------------------------------------
const issuerCache = new Map<string, Promise<IssuerDocument>>();
let trust: Promise<{ customers_url: string; aliases: Record<string, string> }> | null = null;
function resolveIssuer(issuer: string): Promise<IssuerDocument> {
  let p = issuerCache.get(issuer);
  if (!p) {
    p = (async () => {
      // PROTOTYPE: customers' documents come from this PageID server (the
      // company checks then verify them against Concordium). Production ships
      // the list in the app and also reads the domain's .well-known file.
      trust ??= fetch('./trust.json', { cache: 'no-store' }).then((r) => r.json());
      const t = await trust;
      const id = t.aliases[issuer] ?? issuer;
      if (!/^[a-z0-9-]{1,40}$/.test(id)) throw new Error(`${issuer} is not a PageID customer`);
      const res = await fetch(new URL(t.customers_url.replace('{id}', id), location.href).toString(), { cache: 'no-store' });
      if (!res.ok) throw new Error(`${issuer} is not a PageID customer (HTTP ${res.status})`);
      const doc = (await res.json()) as IssuerDocument;
      // Packets name the customer id; the document must be that customer's.
      if (doc.issuer !== id) throw new Error(`the document is for ${doc.issuer}, not ${id}`);
      return { ...doc, issuer };
    })();
    p.catch(() => issuerCache.delete(issuer));
    issuerCache.set(issuer, p);
  }
  return p;
}

// ---- camera --------------------------------------------------------------
async function startCamera() {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    $('start-error').textContent = 'The camera needs HTTPS (or localhost). Open this page over https://… — see the README.';
    return;
  }
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    });
  } catch (e) {
    $('start-error').textContent = `Camera not available: ${(e as Error).message}`;
    return;
  }
  const video = $<HTMLVideoElement>('video');
  video.srcObject = stream;
  await video.play();
  resetScan();
  scanning = true;
  show('scan');
  updateProgress();
  pump();
}

function resetScan() {
  collector.reset();
  liveness.reset();
  ticks.reset();
  sess = null;
  tickState = null;
  copyCheck = null;
  stopPolling();
  doneStreams.clear();
  lastDone = null;
  live = liveness.state();
  decodedQrs = 0;
  lastRank = 0;
  lastRankAt = performance.now();
  lastPacketBytes = null;
}

function stopCamera() {
  scanning = false;
  stream?.getTracks().forEach((t) => t.stop());
  stream = null;
}

function pump() {
  if (!scanning) return;
  const video = $<HTMLVideoElement>('video');
  const next = () => ('requestVideoFrameCallback' in video ? (video as HTMLVideoElement & { requestVideoFrameCallback(cb: () => void): void }).requestVideoFrameCallback(pump) : requestAnimationFrame(pump));
  if (!busy && video.readyState >= 2 && video.videoWidth) {
    busy = true;
    createImageBitmap(video)
      .then((bitmap) => worker.postMessage({ bitmap, maxSide: 960 }, [bitmap]))
      .catch(() => (busy = false));
  }
  next();
}

worker.onmessage = (e: MessageEvent<{ texts: string[]; engine: string }>) => {
  busy = false;
  engine = e.data.engine;
  for (const t of e.data.texts) feedText(t);
};

let checking: Promise<void> = Promise.resolve();

/** One QR text in. Each newly completed packet goes to the liveness check; also exposed for tests. */
function feedText(text: string): boolean {
  if (!scanning && document.body.dataset.screen !== 'scan') return false;
  decodedQrs++;
  const packet = collector.addText(text);
  const id = collector.current?.stream;
  const raw = collector.lastFrame?.tick;
  if (raw) {
    try {
      ticks.add(decodeTick(raw), nowC());
    } catch {}
    if (sess && !packet) judgeTicks();
  }
  updateProgress();
  if (packet && id !== undefined && (!doneStreams.has(id) || id !== lastDone)) {
    doneStreams.add(id);
    lastDone = id;
    const seenAt = Date.now();
    checking = checking.then(() => livePacket(packet, seenAt));
    return true;
  }
  return false;
}

/** Signature first (so iat and nonce can be trusted), then the liveness rule. */
async function livePacket(bytes: Uint8Array, seenAt: number) {
  if (document.body.dataset.screen !== 'scan') return;
  let p: NetIdPacket;
  try {
    const env = parseEnvelope(bytes);
    const doc = await resolveIssuer(env.issuer);
    const k = findKey(doc, env.kid, seenAt);
    if ('error' in k) throw new Error(k.error);
    if (!verifyEnvelope(env, k.key)) throw new Error('bad signature');
    p = openPayload(env);
  } catch {
    // Not signed by a key we can check: let the full verification explain why.
    stopCamera();
    void verify(bytes);
    return;
  }
  lastPacketBytes = bytes;
  if (p.netid === 3 && p.session) {
    // A v3 code: one verified packet names the session and its key; the signed ticks do the rest.
    if (!sess || sess.idText !== p.session.id) {
      ticks.reset();
      tickState = null;
    }
    sess = { id: fromBase64Url(p.session.id), pub: fromBase64Url(p.session.pub), idText: p.session.id, packet: p, bytes };
    judgeTicks();
    return;
  }
  sess = null;
  live = liveness.add({ iat: p.iat, nonce: p.nonce, seenAt, subject: `${p.issuer}|${p.kid}|${p.page ?? ''}|${p.uid ?? ''}|${p.version ?? ''}` });
  updateProgress();
  if (live.state === 'live' || live.state === 'replay') {
    stopCamera();
    copyCheck = Promise.resolve({ state: 'legacy' });
    void verify(bytes);
  }
}

/** The tick rule for the current v3 session: live → verify; stale / replay / cadence → verify (it fails) and tell PageID. */
function judgeTicks() {
  if (!sess || copyCheck || document.body.dataset.screen !== 'scan') return;
  tickState = ticks.evaluate(sess, !!clock);
  updateProgress();
  if (tickState.state === 'collecting') return;
  stopCamera();
  copyCheck = reportScan(sess.packet, tickState);
  void verify(sess.bytes);
}

const COPY_TIMEOUT_MS = 4000;
/** POST /api/scan-seen {session, iat, scanner_id, tick, page[, anomaly]}: the decoded signed tick proves this phone saw the code. */
async function reportScan(p: NetIdPacket, t: TickState): Promise<CopyCheck> {
  if (!t.last || !p.session) return { state: 'offline' };
  const body = { session: p.session.id, iat: p.iat, scanner_id: scannerId, tick: toBase64Url(encodeTick(t.last)), page: p.page ?? '', ...(t.anomaly ? { anomaly: t.anomaly } : {}) };
  try {
    const r = await fetch('/api/scan-seen', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(COPY_TIMEOUT_MS) });
    if (!r.ok) return { state: 'offline' };
    return (await r.json()) as CopyCheck;
  } catch {
    return { state: 'offline' };
  }
}

/** After a result, ask again for ~30 s: a session revoked after this scan (more phones scanned the copy) turns the screen red. */
let pollTimer: ReturnType<typeof setTimeout> | null = null;
function stopPolling() {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
}
const POLL_FOR_MS = 30_000;
function pollCopy(r: ScanResult, until = Date.now() + POLL_FOR_MS) {
  stopPolling();
  if (!r.packet?.session || Date.now() >= until || r.copy.state === 'shared' || r.copy.state === 'revoked') return;
  pollTimer = setTimeout(async () => {
    try {
      const res = await fetch(`/api/scan-seen/${r.packet!.session!.id}?scanner=${scannerId}`, { cache: 'no-store' });
      const c = (await res.json()) as CopyCheck;
      if (lastResult !== r) return;
      if (c.state === 'shared' || c.state === 'revoked') {
        r.copy = c;
        r.verdict = 'bad';
        showInvalid(r);
        return;
      }
    } catch {}
    if (lastResult === r) pollCopy(r, until);
  }, POLL_MS);
}
let POLL_MS = 10_000;

let lastRank = 0;
let lastRankAt = 0;
function updateProgress() {
  const d = collector.current;
  // Progress: v3 = the packet's pieces, then the live ticks; older codes = the liveness codes plus the current one's pieces.
  const per = 1 / live.min;
  const pct = sess
    ? Math.min(100, Math.round(50 + 50 * Math.min(1, (tickState?.fresh ?? 0) / ticks.o.min)))
    : Math.min(100, Math.round((live.count * per + (d && !d.done ? d.progress * per : 0)) * 100));
  $<HTMLElement>('bar').style.width = `${pct}%`;
  const ring = $('ring');
  ring.setAttribute('aria-valuenow', String(pct));
  (ring.querySelector('.ring-fill') as SVGCircleElement).style.strokeDasharray = `${pct} 100`;
  $('ring-pct').textContent = `${pct}%`;
  const now = performance.now();
  if (d && d.rank !== lastRank) {
    lastRank = d.rank;
    lastRankAt = now;
  }
  if (!d) {
    $('progress-text').textContent = 'Looking for a PageID code…';
    $('progress-sub').textContent = 'Fit the moving code inside the frame';
    return;
  }
  $('progress-text').textContent = sess ? `Live check: ${tickState?.fresh ?? 0} of ${ticks.o.min} live ticks` : live.count ? `Live check: ${live.count} of ${live.min} fresh codes` : `${d.rank} of ${d.k} pieces`;
  const stalled = now - lastRankAt > 2500;
  $('progress-sub').textContent = stalled ? 'Move a little closer, or tilt to cut glare' : live.count ? 'The code re-signs itself every few seconds…' : 'Hold steady…';
}

// ---- verification ---------------------------------------------------------
const fetchFn: Fetch = (url, init) => fetch(url, init);

async function verify(packet: Uint8Array) {
  show('verifying');
  let v: Verification;
  try {
    await Promise.race([clockReady, new Promise((r) => setTimeout(r, 1500))]);
    v = await verifyPacketBytes(packet, { resolveIssuer, fetch: fetchFn, now: nowC() });
  } catch (e) {
    v = { genuine: false, checks: [], packet: null, record: null, tx: null, explorer: null, error: (e as Error).message };
  }
  // The liveness rule is a required check of its own: v3 = the signed ticks (2.5 s), older codes = ≥ 3 fresh packets.
  const t = v.packet?.netid === 3 ? tickState : null;
  const isLive = t ? t.state === 'live' : live.state === 'live';
  if (v.packet) {
    v.checks.splice(2, 0, {
      key: 'live',
      label: 'Live, not a recording',
      ok: isLive,
      required: true,
      detail: t
        ? t.state === 'live' ? `${t.message} (${t.detail})` : t.state === 'collecting' ? `only ${t.fresh} of ${t.min} live ticks were seen` : `${t.message} ${t.detail}`
        : live.state === 'live' ? `${live.message} (${live.detail})` : live.state === 'replay' ? `${live.message} ${live.detail}` : `only ${live.count} of ${live.min} fresh codes were seen`,
    });
    v.genuine = v.genuine && isLive;
  }
  // Layer 2: what PageID's server knows about this session (the report went out when the ticks were judged).
  const copy: CopyCheck = v.packet ? await (copyCheck ?? Promise.resolve<CopyCheck>({ state: v.packet.netid === 3 ? 'offline' : 'legacy' })) : { state: 'legacy' };
  const copied = copy.state === 'shared' || copy.state === 'revoked';
  if (v.packet) {
    v.checks.splice(3, 0, {
      key: 'copy',
      label: 'Not a copy shown to others',
      ok: copy.state === 'unique',
      required: copied,
      detail: copyDetail(copy),
    });
    if (copied) v.genuine = false;
  }
  let company: CompanyVerification | null = null;
  if (v.packet && v.issuerDoc && v.signingKey) {
    try {
      company = await verifyCompany({
        issuerDoc: v.issuerDoc,
        packet: v.packet,
        signingKey: v.signingKey,
        fetch: fetchFn,
        verifyProof: async (c) => {
          const { verifyCompanyProof } = await import('../../../packages/core/src/proof-verify.ts');
          return verifyCompanyProof(c.proof!, { network: c.network, fetch: fetchFn, pinned: c.credential ?? null });
        },
      });
    } catch (e) {
      company = null;
      v.error = `company checks: ${(e as Error).message}`;
    }
  }
  const verdict: ScanResult['verdict'] = !v.genuine || company?.status === 'failed' ? 'bad' : company?.status === 'verified' ? 'ok' : 'pending';
  let address: AddressCheck | null = null;
  try {
    address = v.packet?.page ? addressCheck(v.packet.page) : null;
  } catch {
    address = null;
  }
  lastResult = { ...v, live, ticks: t, copy, company, verdict, address, confirmed: null };
  // A copied code: the red warning, never a certificate. A code that passed goes through the address
  // question first; a failed one goes straight to "Not verified".
  if (copied || verdict === 'bad') showInvalid(lastResult);
  else if (address) askAddress(lastResult);
  else {
    renderProof(lastResult);
    show('proof');
  }
  pollCopy(lastResult);
}

function copyDetail(c: CopyCheck): string {
  switch (c.state) {
    case 'unique':
      return `only this phone has scanned this page view's code${c.near_page ? ' (from the same network as the page: nearby)' : ''}`;
    case 'shared':
      return `${c.scanners ?? 'several'} phones scanned this page view's code`;
    case 'shared-nearby':
      return 'one other phone nearby scanned this same code';
    case 'revoked':
      return `PageID revoked this code's session (${c.reason ?? 'abuse'})`;
    case 'session-unknown':
      return "PageID's server does not know this code's session";
    case 'expired':
      return "this code's session had already ended";
    case 'offline':
      return "PageID's server could not be asked";
    default:
      return 'an older code without a session: not checked';
  }
}

/** The amber line for a code that passed but whose copy check is not a clean "unique" (null: nothing to say). */
type Note = { lead: string; title: string; text: string };
function copyNote(c: CopyCheck): Note | null {
  if (c.state === 'shared-nearby') return { lead: 'Someone else scanned this code too. Fine if they are next to you.', title: 'Another phone scanned this code', text: "Another phone scanned this same code a moment ago. That's fine if it was someone next to you. If not, the page may be a copy." };
  if (c.state === 'offline') return { lead: "Be careful: we couldn't check whether this code is a copy.", title: "Couldn't check for copies", text: "PageID's server did not answer, so your phone could not check whether this code is also being shown to others. Check the address bar with extra care." };
  if (c.state === 'session-unknown' || c.state === 'expired') return { lead: "Be careful: we couldn't check whether this code is a copy.", title: "Couldn't check for copies", text: "PageID doesn't know this code's session any more. Check the address bar with extra care." };
  if (c.state === 'legacy') return { lead: "Be careful: this older code can't be checked for copies.", title: "Couldn't check for copies", text: 'This page uses an older PageID code without its own session, so copies cannot be detected. Check the address bar with extra care.' };
  if (c.datacenter) return { lead: 'Be careful: this code was made on a server, not on a reader’s screen.', title: 'Made on a server network', text: "This code was generated from a server network, not a reader's device. Real pages are opened by people; copies are often run from data centres." };
  return null;
}

const COPY_REASON: Record<string, string> = {
  datacenter: "This code was generated from a server network (a data centre), not from a reader's device. That is how copied pages are run. Using a VPN on the computer that shows the page? Turn it off and reload.",
  'session-flood': 'This code came from a network that asked for hundreds of PageID codes: a copying machine, not a reader.',
  'after-close': 'This code was still being shown after the real page had closed it, as a recording or a delayed copy is.',
  'stale-tick': 'This code was shown seconds after it was made, as a recording or a delayed copy is.',
  'out-of-order': 'An older part of this code came back after a newer one, as a recording does.',
  cadence: 'This code did not move in real time, as a recording or a delayed copy does.',
  'page-mismatch': 'This code was reported from another page than the one it was made for.',
};

/** The one plain line for a failing verdict (the reason, in words anyone understands). */
export function invalidReason(r: Pick<ScanResult, 'confirmed' | 'copy' | 'checks' | 'live' | 'ticks' | 'packet' | 'error' | 'company'>): { kind: string; line: string } {
  if (r.confirmed === false) return { kind: 'address', line: 'Your address bar shows another website: this code was copied onto it.' };
  const c = r.copy;
  if (c.state === 'shared' || c.state === 'revoked') {
    const why = c.state === 'shared' ? 'shared' : (c.reason ?? '');
    if (['shared', 'many-scanners', ''].includes(why)) return { kind: 'copied', line: 'This code is being shown on several screens: a copy.' };
    if (['stale-tick', 'after-close', 'out-of-order', 'cadence'].includes(why)) return { kind: 'recording', line: "It's a recording, not a live code." };
    if (why === 'datacenter') return { kind: 'copied', line: "This code was made on a server, not on a reader's screen: a copy." };
    if (why === 'session-flood') return { kind: 'copied', line: 'It came from a machine that makes copies of codes.' };
    if (why === 'page-mismatch') return { kind: 'copied', line: 'This code was made for another page.' };
    return { kind: 'revoked', line: 'PageID has blocked this code.' };
  }
  const failed = (k: string) => r.checks.find((x) => x.key === k && !x.ok && x.required);
  const sig = failed('signature');
  if (sig || (!r.packet && r.error)) {
    const d = `${sig?.detail ?? ''} ${r.error ?? ''}`;
    if (/does not verify/.test(d)) return { kind: 'signature', line: "The signature doesn't match." };
    if (/expired|not valid yet|revoked/.test(d)) return { kind: 'key', line: "The code's key is no longer valid." };
    if (/not one of|not a PageID customer|key file is for|document is for/.test(d)) return { kind: 'key', line: "It isn't signed with a known PageID key." };
    return { kind: 'unreadable', line: "This isn't a PageID code we can read." };
  }
  if (failed('live')) return { kind: 'recording', line: "It's a recording, not a live code." };
  const fresh = failed('fresh');
  if (fresh) return /future/.test(fresh.detail) ? { kind: 'clock', line: "The code's time doesn't match this phone's clock." } : { kind: 'expired', line: 'The code has expired.' };
  if (failed('hash')) return { kind: 'article', line: "The article's text has been changed." };
  if (failed('chain') || failed('record') || failed('sender')) return { kind: 'article', line: "The article isn't registered on the blockchain as it claims." };
  if (r.company?.status === 'failed') {
    const rev = r.company.checks.find((x) => x.key === 'revocation' && x.state === 'fail');
    return rev ? { kind: 'company', line: 'The company has withdrawn this registration.' } : { kind: 'company', line: "The company behind it didn't pass PageID's checks." };
  }
  return { kind: 'failed', line: "It didn't pass PageID's checks." };
}

/** Any failing verdict: ONE full-screen red result. Company, domain and checks only behind "Technical details". */
function showInvalid(r: ScanResult) {
  stopPolling();
  const why = invalidReason(r);
  const sec = $('invalid');
  sec.dataset.kind = why.kind;
  $('invalid-reason').textContent = why.line;
  const report = $<HTMLButtonElement>('invalid-report');
  const note = $('invalid-report-note');
  const canReport = r.confirmed === false && !!r.address;
  report.hidden = !canReport;
  note.hidden = !canReport;
  if (canReport) {
    report.disabled = false;
    report.textContent = 'Report this';
    delete report.dataset.state;
    note.textContent = "Sends only the code's site, session id and the time. Nothing about you.";
  }
  ($('invalid-tech') as HTMLDetailsElement).open = false;
  const body = $('invalid-tech-body');
  renderProof({ ...r, verdict: 'bad' }, body);
  // The details repeat the proof screen's parts: keep their ids unique.
  for (const n of body.querySelectorAll('[id]')) n.id = `tech-${n.id}`;
  for (const n of body.querySelectorAll('[aria-labelledby]')) n.setAttribute('aria-labelledby', `tech-${n.getAttribute('aria-labelledby')}`);
  const more = r.copy.state === 'revoked' ? COPY_REASON[r.copy.reason ?? ''] : null;
  if (more) body.prepend(el('p', { class: 'card copied-reason' }, more));
  show('invalid');
  window.scrollTo(0, 0);
  $('invalid-title').focus({ preventScroll: true });
}

function noteBox(n: Note, id: string) {
  return el('div', { class: 'copy-note', id, role: 'note' }, svg(ICON.warn), el('div', {}, el('p', { class: 'copy-lead' }, n.lead), el('strong', {}, n.title), el('p', {}, n.text)));
}

// ---- the address step (relay protection, layer 1) -------------------------------------
const setDom = (scope: ParentNode, domain: string) => scope.querySelectorAll<HTMLElement>('[data-dom]').forEach((n) => (n.textContent = domain));

function askAddress(r: ScanResult) {
  const a = r.address!;
  const d = $('addr-domain');
  d.textContent = a.registrable;
  d.style.setProperty('--len', String(a.registrable.length));
  // The full page address underneath, as an address bar: the registrable domain bold, the rest quiet.
  const u = new URL(a.url);
  const bar = $('addr-url');
  bar.replaceChildren(
    svg('<rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="2.2"/><path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>'),
    el('span', {}, el('span', { class: 'quiet' }, `${u.protocol}//${a.subdomain ? `${a.subdomain}.` : ''}`), el('b', {}, a.registrable), el('span', { class: 'quiet' }, `${u.pathname}${u.search}`)),
  );
  const warn = $('addr-warn');
  warn.hidden = !a.warnings.length;
  warn.className = `addr-warn${a.mixedScript ? ' bad' : ''}`;
  warn.replaceChildren(...a.warnings.map((w) => el('p', {}, w)));
  setDom($('address'), a.registrable);
  const note = copyNote(r.copy);
  $('addr-copy').replaceChildren(...(note ? [noteBox(note, 'addr-copy-note')] : []));
  $('addr-copy').hidden = !note;
  const ex = subdomainExamples(a.registrable);
  $('addr-sub').replaceChildren(
    'Subdomains count: ',
    el('b', { class: 'ok-ex', translate: 'no' }, ex.fine), ' is fine. ',
    el('b', { class: 'bad-ex', translate: 'no' }, ex.notFine), ' is NOT: its real address ends in ', el('b', { translate: 'no' }, 'evil.com'), '.',
  );
  show('address');
  window.scrollTo(0, 0);
  $('addr-q').focus({ preventScroll: true });
}

function answerAddress(yes: boolean) {
  const r = lastResult;
  if (!r || document.body.dataset.screen !== 'address') return;
  r.confirmed = yes;
  if (yes) {
    renderProof(r);
    show('proof');
    return;
  }
  showInvalid(r);
}

/** Posts {domain of the code, the code's session id (its nonce), the time}: nothing about the reader. */
async function reportRelay() {
  const r = lastResult;
  const btn = $<HTMLButtonElement>('invalid-report');
  const note = $('invalid-report-note');
  if (!r?.address || !r.packet || btn.disabled) return;
  btn.disabled = true;
  btn.textContent = 'Sending…';
  try {
    const res = await fetch('/api/report-relay', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ domain: r.address.registrable, session: r.packet.nonce, at: new Date().toISOString() }) });
    if (res.status === 202) {
      btn.textContent = 'Reported';
      btn.dataset.state = 'sent';
      note.textContent = 'Thank you. We will look at it. Now close the page you scanned.';
    } else {
      btn.disabled = res.status === 429;
      btn.textContent = res.status === 429 ? 'Not sent' : 'Report this';
      note.textContent = res.status === 429 ? 'Too many reports from this network just now; it was not sent. Close the page anyway.' : 'The report could not be sent. Close the page anyway.';
    }
  } catch {
    btn.disabled = false;
    btn.textContent = 'Report this';
    note.textContent = 'No connection: the report was not sent. Close the page anyway.';
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  n.append(...kids);
  return n;
}

const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/Copenhagen', dateStyle: 'medium', timeStyle: 'short' }) : '—';

const SVG_NS = 'http://www.w3.org/2000/svg';
function svg(markup: string, viewBox = '0 0 24 24'): SVGSVGElement {
  const s = document.createElementNS(SVG_NS, 'svg');
  s.setAttribute('viewBox', viewBox);
  s.setAttribute('aria-hidden', 'true');
  s.innerHTML = markup;
  return s;
}
const ICON = {
  ok: '<path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>',
  bad: '<path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>',
  warn: '<path d="M12 6v8M12 18.5v.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>',
  wait: '<circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-dasharray="3 3"/>',
};

/** One plain sentence per check (SPEC.md §7); the technical detail stays available below. */
function plainLine(c: Check, issuer: string): string {
  const ok: Record<string, string> = {
    signature: `Signed with ${issuer}'s PageID key.`,
    fresh: 'Made seconds ago.',
    live: 'Signed moments ago, in step with this phone: not a screenshot or a recording.',
    copy: 'Only your phone has scanned this page view: not a copy shown to others.',
    hash: 'The text is exactly the text that was signed.',
    chain: 'Registered and finalized on the Concordium blockchain.',
    record: 'The blockchain record names this text, story and version.',
    sender: `Registered from ${issuer}'s own Concordium account.`,
    'second-source': 'A second, independent Concordium service agrees.',
  };
  const bad: Record<string, string> = {
    signature: `Not signed with a key ${issuer} has published.`,
    fresh: 'The code is not fresh: it may be a recording or a screenshot.',
    live: 'This looks like a replayed recording or a screenshot, not a live code.',
    copy: 'This code may also be shown to others: see above.',
    hash: 'The text has been changed: its fingerprint does not match.',
    chain: 'No finalized Concordium registration was found.',
    record: 'The blockchain record does not match this text.',
    sender: `Registered by an account that is not ${issuer}'s.`,
    'second-source': 'A second Concordium service could not confirm it (optional).',
  };
  return (c.ok ? ok : bad)[c.key] ?? c.detail;
}

function checkItem(kind: 'ok' | 'bad' | 'warn' | 'wait', key: string, label: string, line: string, detail: string | null, optional: boolean) {
  const mark = el('span', { class: 'mark', 'aria-hidden': 'true' });
  mark.append(svg(ICON[kind]));
  const body = el('div', {}, el('strong', {}, label, ...(optional ? [' ', el('span', { class: 'opt' }, '· optional')] : [])), el('div', { class: 'plain' }, line));
  if (detail) body.append(el('div', { class: 'detail' }, detail));
  return el('li', { class: kind, 'data-key': key, 'data-state': kind, 'aria-label': `${label}: ${kind === 'ok' ? 'passed' : kind === 'wait' ? 'pending' : 'failed'}` }, mark, body);
}

function companyCard(c: CompanyVerification | null, issuerDoc: IssuerDocument | null | undefined): HTMLElement {
  const status = c?.status ?? 'pending';
  const card = el('section', { class: `card company ${status}`, id: 'company', 'data-status': status, 'aria-labelledby': 'company-title' });
  const name = status === 'verified' ? c!.legalName! : (issuerDoc?.name ?? 'Unknown company').replace(/ \(pending onboarding\)$/, '');
  card.append(
    el('h2', { class: 'card-title', id: 'company-title' }, 'Company'),
    el('div', { class: 'issuer-row' },
      el('span', { class: 'issuer-badge', 'aria-hidden': 'true' }, name.replace(/[^A-Za-z0-9 ]/g, '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?'),
      el('div', {}, el('div', { class: 'who' }, name), el('div', { class: 'meta' }, status === 'verified' ? [c!.businessNumber && `${regNumberLabel(c!.legalCountry).short} ${c!.businessNumber}`, c!.legalCountry, c!.lei && `LEI ${c!.lei}`].filter(Boolean).join(' · ') : status === 'pending' ? 'Pending onboarding: not verified yet' : 'Company identity failed')),
      el('span', { class: `badge${status === 'verified' ? '' : status === 'pending' ? ' wait' : ' bad'}` }, status === 'verified' ? 'Verified' : status === 'pending' ? 'Pending' : 'Not verified')),
  );
  if (status === 'verified') card.append(el('p', { class: 'company-line' }, `Verified by ${c!.idpName ?? 'its identity provider'} through Concordium; the company bound this page's domain and key on chain.`));
  else if (status === 'pending') card.append(el('p', { class: 'company-line' }, `${name} has not finished PageID onboarding, so who runs this page is NOT verified yet. Treat the name above as a claim.`));
  if (c?.checks.length) {
    const list = el('ul', { class: 'checks', id: 'company-checks' });
    for (const x of c.checks as CompanyCheck[]) list.append(checkItem(x.state === 'ok' ? 'ok' : x.state === 'pending' ? 'wait' : x.required ? 'bad' : 'warn', x.key, x.label, x.line, x.state === 'fail' ? x.detail : null, !x.required));
    card.append(el('details', { class: 'company-more' }, el('summary', {}, `${c.checks.filter((x) => x.state === 'ok').length} of ${c.checks.length} company checks passed`), list));
  }
  return card;
}

function renderProof(v: ScanResult, root: HTMLElement = $('proof-body')) {
  root.replaceChildren();
  const p = v.packet;
  const who = v.company?.status === 'verified' ? v.company.legalName! : (v.issuerDoc?.name ?? p?.issuer ?? 'the issuer').replace(/ \(pending onboarding\)$/, '');
  const failed = v.checks.filter((c) => !c.ok && c.required);

  const seal = el('div', { class: 'seal' });
  seal.append(
    v.verdict === 'ok'
      ? svg('<circle cx="38" cy="38" r="36.5" fill="none" stroke="url(#g-ok)" stroke-width="1.5" stroke-dasharray="1.5 3"/><circle cx="38" cy="38" r="30" fill="url(#g-ok)"/><circle cx="38" cy="38" r="30" fill="none" stroke="#fff" stroke-opacity=".4"/><path class="draw" d="M26 38.5l8 8 16-17" fill="none" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>', '0 0 76 76')
      : v.verdict === 'pending'
        ? svg('<circle cx="38" cy="38" r="34" fill="var(--info-soft)" stroke="var(--info)" stroke-opacity=".6" stroke-width="2" stroke-dasharray="5 5"/><path d="M38 22v18l10 6" fill="none" stroke="var(--info)" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>', '0 0 76 76')
        : svg('<circle cx="38" cy="38" r="34" fill="var(--bad-soft)" stroke="var(--bad)" stroke-opacity=".6" stroke-width="2"/><path d="M38 23v20M38 52v1" fill="none" stroke="var(--bad)" stroke-width="5" stroke-linecap="round"/>', '0 0 76 76'),
  );
  const title = v.verdict === 'ok' ? 'Verified' : v.verdict === 'pending' ? 'Company not verified yet' : v.live.state === 'replay' ? 'Not a live code' : 'Not verified';
  const tagline =
    v.verdict === 'ok'
      ? `This live code comes from ${who}, verified on Concordium. Your phone checked everything itself.`
      : v.verdict === 'pending'
        ? `A live, correctly signed code, but ${who} has not finished PageID onboarding. Who runs this page is not verified.`
        : v.live.state === 'replay'
          ? `${v.live.message} ${v.live.detail}.`
          : failed.length
            ? `${failed.length} of ${v.checks.filter((c) => c.required).length} required checks failed: ${failed.map((c) => c.label.toLowerCase()).join(', ')}. Do not trust it.`
            : v.company?.status === 'failed'
              ? `${v.company.summary}. Do not trust it.`
              : (v.error ?? 'This code could not be verified.');
  const co = v.company?.status === 'verified' ? v.company : null;
  const ids = el('dl', { class: 'cert-ids' });
  const id = (k: string, val: string) => ids.append(el('div', {}, el('dt', {}, k), el('dd', {}, val)));
  if (co) {
    if (co.businessNumber) id(regNumberLabel(co.legalCountry).short, co.businessNumber);
    if (co.legalCountry) id('Country', co.legalCountry);
    if (co.lei) id('LEI', co.lei);
  }
  const badge = v.verdict === 'ok' ? el('span', { class: 'badge' }, 'Verified') : v.verdict === 'pending' ? el('span', { class: 'badge wait' }, 'Pending') : el('span', { class: 'badge bad' }, 'Not verified');
  const checkedAt = new Date().toLocaleString('en-GB', { timeZone: 'Europe/Copenhagen', dateStyle: 'medium', timeStyle: 'medium' });
  // Never a good-looking certificate without the confirmed address: the domain is part of what is verified.
  const dom = v.verdict !== 'bad' && v.confirmed === true && v.address ? v.address.registrable : null;
  const summary = dom
    ? el('p', { class: 'cert-summary', id: 'cert-summary' }, v.verdict === 'ok' ? 'Verified company' : 'Company not verified yet', ' · ', el('b', {}, who), v.verdict === 'ok' ? '' : ' (claimed)', ' · for the page on ', el('b', { class: 'dom', translate: 'no' }, dom))
    : null;
  const cert = el('article', { class: `cert proof-cert verdict ${v.verdict === 'ok' ? 'ok' : v.verdict === 'pending' ? 'pending' : 'bad'}`, id: 'verdict', 'data-verdict': v.verdict, 'data-genuine': String(v.genuine), 'data-confirmed': String(v.confirmed), 'aria-labelledby': 'verdict-title' },
    el('div', { class: 'cert-top' }, el('span', { class: 'cert-kind' }, 'PageID · Verification certificate'), badge),
    el('div', { class: 'row', style: 'flex-wrap:nowrap;gap:16px;align-items:flex-start' },
      el('div', { style: 'flex:1;min-width:0' },
        el('h1', { class: 'verdict-title', id: 'verdict-title' }, title),
        el('p', { class: 'cert-kind', style: 'margin:14px 0 6px' }, co ? 'This page is run by' : p ? 'This code names' : 'Code'),
        el('p', { class: 'cert-name' }, p ? who : 'Unreadable code'),
        ...(co ? [el('p', { class: 'cert-sub' }, `as verified by ${co.idpName ?? 'its identity provider'} through Concordium`)] : p ? [el('p', { class: 'claimed-note' }, 'Claimed name: not verified')] : [])),
      seal),
  );
  if (summary) cert.append(summary);
  // An amber state leads with its plain sentence, above the certificate.
  const cn = v.verdict !== 'bad' ? copyNote(v.copy) : null;
  if (cn) root.append(noteBox(cn, 'cert-copy-note'));
  if (dom)
    cert.append(
      el('div', { class: 'cert-domain' },
        el('p', { class: 'cert-kind' }, 'For the page on'),
        el('p', { class: 'cert-dom', translate: 'no', style: `--len:${dom.length}` }, dom),
        el('p', { class: 'cert-confirmed', id: 'cert-confirmed' }, svg(ICON.ok), el('span', {}, 'You confirmed the address: ', el('b', { translate: 'no' }, dom)))),
    );
  if (co) cert.append(ids);
  cert.append(el('p', { class: 'tagline' }, tagline));
  if (v.checks.length) {
    const passed = v.checks.filter((c) => c.ok).length + (v.company?.checks.filter((c) => c.state === 'ok').length ?? 0);
    const total = v.checks.length + (v.company?.checks.length ?? 0);
    cert.append(el('div', { class: 'cert-foot' }, el('span', {}, el('b', {}, `${passed} of ${total}`), ' checks passed on this phone'), el('span', {}, el('b', {}, checkedAt))));
  }
  root.append(cert);

  if (p) root.append(companyCard(v.company, v.issuerDoc));

  if (p?.page) {
    const u = new URL(p.page);
    root.append(
      el('section', { class: 'card page', 'aria-label': 'Page' },
        el('h2', { class: 'card-title' }, 'This code is for'),
        el('p', { class: 'page-url' }, el('b', {}, u.hostname), u.pathname + u.search),
        el('p', { class: 'page-hint' }, v.confirmed === true ? "You said your browser's address bar shows this address. If it does not, close the page: the code was copied onto another site." : 'Check that this is the address in your browser. A code copied onto another page still shows the address it was made for.')),
    );
  }

  if (p && isArticle(p)) {
    const lines = p.doc.body.split('\n').filter(Boolean);
    const ok = v.genuine;
    const story = el('section', { class: `card story${ok ? '' : ' warn-edge'}`, 'aria-label': 'Article' },
      el('h2', { class: 'card-title' }, 'Article'),
      el('div', { class: 'issuer-row' },
        el('div', {}, el('div', { class: 'who' }, fmtTime(p.doc.published_at)), el('div', { class: 'meta' }, `version ${p.version}`)),
        el('span', { class: `badge${ok ? '' : ' bad'}` }, ok ? 'Authentic text' : 'Unverified')),
    );
    if (!ok) story.append(el('p', { class: 'claimed' }, 'Claimed headline: this text did not pass the checks'));
    story.append(el('h2', { id: 'story-title' }, p.doc.title), el('p', { class: 'lede' }, lines.slice(0, 2).join(' ')));
    root.append(story);
  }

  const checksCard = el('section', { class: 'card', 'aria-labelledby': 'checks-title' }, el('h2', { class: 'card-title', id: 'checks-title' }, `Code: ${v.checks.filter((c) => c.ok).length} of ${v.checks.length} checks passed`));
  const list = el('ul', { class: 'checks', id: 'checks' });
  for (const c of v.checks) list.append(checkItem(c.ok ? 'ok' : c.required ? 'bad' : 'warn', c.key, c.label, plainLine(c, who), c.ok ? null : c.detail, !c.required));
  if (v.error && !v.checks.length) list.append(checkItem('bad', 'read', 'Could not read the code', v.error, null, false));
  checksCard.append(list);
  root.append(checksCard);

  if (p) {
    const facts = el('dl', { class: 'facts' });
    const fact = (k: string, val: Node | string) => facts.append(el('dt', {}, k), el('dd', {}, val));
    fact('Customer', p.issuer);
    if (p.company) fact('Company account', el('code', {}, p.company));
    if (isArticle(p)) {
      const copy = el('button', { class: 'copy', type: 'button', 'aria-label': 'Copy SHA-256' }, 'Copy');
      copy.addEventListener('click', () => {
        void navigator.clipboard?.writeText(p.sha256).then(() => {
          copy.textContent = 'Copied';
          copy.classList.add('done');
          setTimeout(() => {
            copy.textContent = 'Copy';
            copy.classList.remove('done');
          }, 1600);
        });
      });
      fact('SHA-256', el('div', { class: 'hash' }, el('code', {}, p.sha256), copy));
      fact('Block', v.tx?.blockHeight ? `${v.tx.blockHeight.toLocaleString('en-GB')}` : '—');
      fact('Block time', fmtTime(v.tx?.blockTime));
      fact('Address', p.doc.url ?? '—');
      fact('Transaction', el('code', {}, `${p.chain.tx_hash.slice(0, 20)}…`));
    }
    fact('Signed', `${new Date(p.iat).toLocaleTimeString('en-GB')} · ${p.kid}`);
    const tech = el('ul', { class: 'tech', 'aria-label': 'Technical detail per check' });
    for (const c of v.checks) tech.append(el('li', {}, el('b', {}, `${c.key}: `), c.detail));
    for (const c of v.company?.checks ?? []) tech.append(el('li', {}, el('b', {}, `company.${c.key}: `), c.detail));
    root.append(el('details', { class: 'card more' }, el('summary', {}, 'Details'), facts, tech));

    const actions = el('div', { class: 'actions' });
    if (v.explorer) {
      const a = el('a', { class: 'btn', href: v.explorer, target: '_blank', rel: 'noopener', 'aria-label': 'View the transaction on CCDScan (opens a new tab)' }, 'View on CCDScan');
      a.append(svg('<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'));
      a.lastElementChild!.setAttribute('class', 'btn-icon');
      actions.append(a);
    }
    root.append(actions);

    if (isArticle(p)) {
      const full = el('details', { class: 'full' }, el('summary', {}, 'Read the full text'));
      full.append(el('div', { class: 'text' }, el('h3', {}, p.doc.title), ...p.doc.body.split('\n\n').map((para) => el('p', {}, para))));
      root.append(full);
    }
  }
  if (root.id === 'proof-body') window.scrollTo(0, 0);
}

// ---- wiring ----------------------------------------------------------------
$('start-btn').addEventListener('click', () => void startCamera());
$('cancel-btn').addEventListener('click', () => {
  stopCamera();
  show('start');
});
$('again-btn').addEventListener('click', () => void startCamera());
$('invalid-again').addEventListener('click', () => void startCamera());
$('addr-yes').addEventListener('click', () => answerAddress(true));
$('addr-no').addEventListener('click', () => answerAddress(false));
$('invalid-report').addEventListener('click', () => void reportRelay());
show('start');
if (!window.isSecureContext) $('start-error').textContent = 'This page is not on HTTPS: the camera will not start. Use the https:// address (README).';

(window as unknown as { netid: unknown }).netid = {
  feedText,
  /** TESTS: feed QR texts with no camera (puts the scanner in scan mode). */
  startWithoutCamera() {
    resetScan();
    scanning = true;
    show('scan');
    updateProgress();
  },
  get result() {
    return lastResult;
  },
  /** TESTS: show the address question again for the last scan (to try both answers on one scan). */
  askAddress() {
    if (lastResult?.address && lastResult.verdict !== 'bad') {
      lastResult.confirmed = null;
      askAddress(lastResult);
    }
  },
  /** DESIGN SCREENSHOTS ONLY: the address question for a MOCKED page address (look-alike designs). */
  askAddressFor(url: string) {
    if (lastResult) askAddress({ ...lastResult, address: addressCheck(url), confirmed: null });
  },
  /** DESIGN SCREENSHOTS ONLY: re-render the proof screen with a changed result (e.g. a MOCKED verified company). */
  render(r: ScanResult) {
    renderProof(r);
    show('proof');
  },
  get live() {
    return live;
  },
  get ticks() {
    return tickState;
  },
  get clock() {
    return clock;
  },
  scannerId,
  /** TESTS: how often a result screen re-asks PageID about the session (ms). */
  set pollMs(ms: number) {
    POLL_MS = ms;
  },
  /** DESIGN SCREENSHOTS ONLY: show the copied-code warning / an amber copy note for the last scan with a MOCKED copy state. */
  mockCopy(c: CopyCheck) {
    if (!lastResult) return;
    const r = { ...lastResult, copy: c };
    if (c.state === 'shared' || c.state === 'revoked') return showInvalid({ ...r, verdict: 'bad' });
    r.confirmed = true;
    renderProof(r);
    show('proof');
  },
  get lastPacket() {
    return lastPacketBytes;
  },
  get progress() {
    const d = collector.current;
    return d ? { rank: d.rank, k: d.k, frames: d.frames, decoded: decodedQrs, engine, live: live.count } : null;
  },
};

if ('serviceWorker' in navigator && window.isSecureContext) navigator.serviceWorker.register('./sw.js').catch(() => {});
